import { useMemo } from 'react';
import { useGetFilteredUnreadCountsQuery } from '../assetrequest/assetReqApiSlice';
import { useGetFilteredConsumableUnreadCountsQuery } from '../consumablerequest/consumableReqApiSlice';
import { assetInboxTags } from '../../Components/Assets/Myrequests/Inbox/assetInboxTags';
import { consumInboxTags } from '../../Components/Consumables/ConsumablesReq/Inbox/consumInboxTags';
import { complaintInboxTags } from '../../Components/Complaints/CompInbox/compInboxTags';
import useAuth from './useAuth';

/**
 * Custom hook to calculate and provide the total unread count across all inbox tabs
 * Matches the implementation in UnifiedInbox component
 * @returns {Object} { totalUnreadCount: number, isLoading: boolean, error: any }
 */
export const useTotalUnreadCount = () => {
  const Auth = useAuth();

  // Set up the count filter for Asset Requests and Complaints (using assetReqApiSlice)
  const countFilter = useMemo(() => ({
    userId: Auth?.id,
    sent_to_tags: {
      0: assetInboxTags,     // Asset Request tab
      2: complaintInboxTags  // Complaints tab
    },
    req_store: ""
  }), [Auth?.id]);

  // Set up the count filter for Consumable Requests (using consumableReqApiSlice)
  const consumableCountFilter = useMemo(() => ({
    userId: Auth?.id,
    sent_to_tags: {
      1: consumInboxTags,    // Consumable Request tab
    },
    req_store: ""
  }), [Auth?.id]);

  // Fetch filtered counts for Asset Requests and Complaints
  const { data: filteredCounts, isLoading: isAssetLoading, error: assetError } = useGetFilteredUnreadCountsQuery(
    countFilter,
    {
      skip: !Auth?.id,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true
    }
  );

  // Fetch filtered counts for Consumable Requests
  const { data: filteredConsumableCounts, isLoading: isConsumableLoading, error: consumableError } = useGetFilteredConsumableUnreadCountsQuery(
    consumableCountFilter,
    {
      skip: !Auth?.id,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true
    }
  );

  // Calculate total unread count across all tabs and sub-tabs
  const totalUnreadCount = useMemo(() => {
    let totalUnread = 0;

    // Process Asset Request and Complaints counts (tab 0 and 2)
    if (filteredCounts) {
      Object.values(filteredCounts).forEach(mainTabData => {
        if (mainTabData && typeof mainTabData === 'object') {
          Object.values(mainTabData).forEach(subTabData => {
            if (subTabData && typeof subTabData === 'object') {
              totalUnread += subTabData.unread || 0;
            }
          });
        }
      });
    }

    // Process Consumable Request counts (tab 1)
    if (filteredConsumableCounts) {
      Object.values(filteredConsumableCounts).forEach(mainTabData => {
        if (mainTabData && typeof mainTabData === 'object') {
          Object.values(mainTabData).forEach(subTabData => {
            if (subTabData && typeof subTabData === 'object') {
              totalUnread += subTabData.unread || 0;
            }
          });
        }
      });
    }

    return totalUnread;
  }, [filteredCounts, filteredConsumableCounts]);

  return {
    totalUnreadCount,
    isLoading: isAssetLoading || isConsumableLoading,
    error: assetError || consumableError
  };
};

export default useTotalUnreadCount;

